/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useState, useEffect } from 'react';
import Icon from 'components/Icons/Expand/Expand';
import useGuid from 'effects/useGuid';
import useKeys from 'effects/keys';
import useAnimation from './animation';
import './Expandable.scss';

const DEFAULT_LABELS = {
  open: 'Open',
  close: 'Close',
};

interface ExpandableProps {
  heading: string;
  children?: string;
  labels?: { open: string; close: string };
}

/**
 * This is the Expandable component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function Expandable({
  heading,
  children,
  labels = DEFAULT_LABELS,
}: ExpandableProps) {
  const ref = useRef<HTMLDivElement>(null);
  const guid = useGuid('expandable');
  const container = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const animation = useAnimation(container, content);
  const [focus, setFocus] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onFocus() {
      if (!ref.current!.contains(document.activeElement)) {
        setFocus(false);
      }
    }

    if (focus && open) {
      document.body.addEventListener('focusin', onFocus);
    }

    return () => {
      document.body.removeEventListener('focusin', onFocus);
    };
  }, [focus, open]);

  useKeys('expandable', focus && open, {
    esc: e => {
      e.preventDefault();
      setOpen(false);
    },
  });

  useEffect(() => {
    if (open) {
      animation.open();
    } else {
      animation.close();
    }
  }, [open]);

  return (
    <div
      ref={ref}
      className="block expandable"
      onFocus={() => {
        setFocus(true);
      }}
    >
      <div className="content">
        <button
          aria-haspopup="true"
          aria-expanded={open ? 'true' : 'false'}
          aria-controls={`details${guid}`}
          onClick={() => {
            setOpen(open => !open);
          }}
        >
          <span>
            <Icon title={open ? labels.close : labels.open} open={open} />
            <span>{heading}</span>
          </span>
        </button>
        <div
          ref={container}
          className="container"
          id={`details${guid}`}
          aria-labelledby={`button${guid}`}
          onClick={() => {
            setOpen(open => {
              if (!open) {
                return true;
              }
              return open;
            });
          }}
        >
          <div
            ref={content}
            className="body"
            dangerouslySetInnerHTML={{ __html: children! }}
          />
        </div>
      </div>
    </div>
  );
}

export default Expandable;
